import { useEffect, useRef, useState } from "react"
import Image from "next/legacy/image"
import cx from "classnames"
import { useMeta } from "@/lib/contexts/appContext"
import Button from "@/common/button/button"
import AddToCart from "@/components/woocommerce/AddToCart"
import AddToCartVariation from "@/components/woocommerce/AddToCartVariation"
import ProductPrice from "@/components/woocommerce/ProductPrice"
import { currency } from "@/lib/helpers/woocommerce"
import { headerOffsetScroll } from "@/lib/helpers/utils"
import Block from "@/components/wrappers/block"
import styles from "@/components/woocommerce/typography.module.css"
import sliderStyles from "@/components/woocommerce/slider.module.css"

import CheckmarkCircle from "@/common/partials/checkmarkCircle"

import { Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css/bundle"
import Arrow from "@/common/icons/arrow"

const ProductSingle = ({ block }) => {
  const { product } = block.attrs
  const {
    free_price,
    name,
    images,
    prices,
    short_description,
    description,
    add_to_cart,
    type
  } = product

  const contentRef = useRef(null)

  const [mounted, setMounted] = useState(false)
  const [variationId, setVariationId] = useState(0)
  const [price, setPrice] = useState(100)
  const [customPrice, setCustomPrice] = useState(false)
  const [customPrices, setCustomPrices] = useState([])
  const [cheapest, setCheapest] = useState(100)
  const [customPriceError, setCustomPriceError] = useState(false)

  const currentProduct = product.variations.find(
    (product) => +product.id === +variationId
  )

  const handleScroll = () => {
    headerOffsetScroll(contentRef)
  }

  const {
    woocommerce: { i18n: { pdp: { read_more } = {} } = {} }
  } = useMeta()

  const contentClassNames = cx(
    "container max-w-screen-md py-10 sm:py-12.5 lg:py-20 xl:text-xl xl:leading-8 font-serif",
    styles.article
  )

  let custom_prices = block.attrs.product.attributes.find(
    (attribute) => attribute.name === "Priskategori"
  )

  useEffect(() => {
    let customPrices = []
    if (block.attrs.product.free_price && custom_prices) {
      custom_prices.terms.forEach((element) => {
        if (element.name !== "Valfritt") {
          customPrices.push(parseInt(element.name))
        }
      })
      customPrices = customPrices.reverse()
      setCustomPrices(customPrices)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPrice(product.pre_selected_price)
    setCheapest(product.minimum_price)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function setProductPrice(price) {
    setPrice(price)
    setCustomPrice(false)
  }

  const prevRef = useRef(null)
  const nextRef = useRef(null)

  useEffect(() => setMounted(true), [])

  const settings = {
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current
    },
    onBeforeInit: (swiper) => {
      swiper.params.navigation.prevEl = prevRef.current
      swiper.params.navigation.nextEl = nextRef.current
    },
    slidesPerView: 1,
    spaceBetween: 40
  }

  useEffect(() => {
    if (!free_price) {
      setCustomPriceError(false)
      return
    }
    if (price < cheapest) {
      setCustomPriceError(true)
    } else {
      setCustomPriceError(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price])

  return (
    <>
      <Block block={block}>
        <div className="grid grid-cols-1 gap-8 lg:gap-18 xl:gap-26 lg:grid-cols-12">
          <div className="relative lg:col-span-5 xl:col-span-6">
            {mounted && (
              <Swiper
                pagination={true}
                modules={[Navigation, Pagination]}
                {...settings}
                className={sliderStyles.swiper}>
                {images[0] !== null &&
                  images.map((child, index) => {
                    return (
                      <SwiperSlide key={`s-${index}`}>
                        <Image
                          src={child.src}
                          alt={name}
                          layout="responsive"
                          width="374"
                          height="281"
                          className="object-contain"
                          sizes={child.sizes}
                        />
                      </SwiperSlide>
                    )
                  })}
                <div className={sliderStyles.navigation}>
                  <button
                    ref={prevRef}
                    aria-label="Föregående"
                    className={cx(sliderStyles.button, "swiper-nav-prev")}>
                    <Arrow className={sliderStyles.leftArrow} />
                  </button>
                  <button
                    ref={nextRef}
                    aria-label="Nästa"
                    className={cx(sliderStyles.button, "swiper-nav-next")}>
                    <Arrow className={sliderStyles.rightArrow} />
                  </button>
                </div>
              </Swiper>
            )}
          </div>

          <div className="lg:col-span-7 xl:col-span-6 core">
            <h1 className="mb-4">{name}</h1>
            {short_description ? (
              <span
                className={`xl:text-xl xl:leading-8 ${styles.article} font-sans`}
                dangerouslySetInnerHTML={{
                  __html: short_description
                }}></span>
            ) : null}
            {description ? (
              <Button
                size="small"
                theme="text"
                ariaLabel={read_more}
                onClick={handleScroll}
                className="my-4">
                {read_more}
              </Button>
            ) : null}
            <>
              {!free_price && (
                <ProductPrice
                  className="flex mb-4 text-xl sm:text-1.5xl lg:text-2.5xl font-bold"
                  currency={currency}
                  price={currentProduct?.product?.prices?.price ?? prices.price}
                />
              )}
              {free_price && (
                <ProductPrice
                  className="flex mb-4 text-xl sm:text-1.5xl lg:text-2.5xl font-bold"
                  currency={currency}
                  price={price}
                />
              )}
              {free_price && (
                <>
                  <div className="flex flex-col">
                    <div className="grid mb-4 sm:gap-y-2 sm:grid-cols-3">
                      {customPrices.map((amount) => (
                        <div
                          key={"amount-" + amount}
                          className={cx(
                            "flex items-center first:rounded-t last:rounded-b -mt-px sm:-ml-px overflow-hidden border border-black-100/10 sm:first:rounded-l sm:first:rounded-r-none sm:last:rounded-r sm:last:rounded-lnone sm:last:border-r-1 bg-white",
                            {
                              "relative z-10 border-green-100 bg-green-10":
                                price === amount && !customPrice
                            }
                          )}>
                          <label
                            className={cx(
                              "relative flex items-center justify-center w-full h-full px-4 py-3 font-bold text-center  cursor-pointer hover:underline"
                            )}>
                            <input
                              type="radio"
                              value={amount}
                              onChange={() => setProductPrice(amount)}
                              checked={price == amount && !customPrice}
                              className="hidden"
                            />
                            <CheckmarkCircle
                              selected={price == amount && !customPrice}
                            />
                            {amount}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div
                      className={cx(
                        "relative flex items-center overflow-hidden border border-black-100/10 rounded-l",
                        {
                          "border-green-100": customPrice
                        }
                      )}>
                      <label
                        className={cx(
                          "flex items-center w-full h-full px-4 py-3 font-bold bg-white cursor-pointer hover:underline justify-start",
                          {
                            "border-green-100 bg-green-10": customPrice
                          }
                        )}>
                        <input
                          type="radio"
                          value="custom"
                          checked={customPrice}
                          onChange={() => {
                            setPrice(cheapest)
                            setCustomPrice(true)
                          }}
                          className="hidden placeholder-black-100"
                        />
                        <CheckmarkCircle selected={customPrice} />
                        Valfritt
                      </label>
                      {customPrice && (
                        <div className="box-border absolute top-0 flex-1 w-full h-full bold bg-red">
                          <input
                            autoFocus
                            id="customPrice"
                            className="w-[200px] h-full ml-12 font-bold bold bg-green-10 appearance-none"
                            type="number"
                            name="custom_price"
                            min="100"
                            max="1000000"
                            value={price}
                            placeholder="Valfritt belopp"
                            onChange={(e) => {
                              setPrice(e.target.value)
                            }}
                          />
                        </div>
                      )}
                    </div>
                    {customPrice && customPriceError && (
                      <div className="mt-2 text-xs text-red-100">
                        Det minsta beloppet att ange är {cheapest} kr
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
            {type === "variable" ? (
              <>
                <AddToCartVariation
                  product={product}
                  style="button"
                  wrapperClasses={"grid sm:gap-y-2 sm:grid-cols-2"}
                  setCurrentVariationId={setVariationId}
                  customPrice={free_price ? price : false}
                  hideQuantity={free_price}
                  disabledButton={customPriceError}
                />
              </>
            ) : (
              <AddToCart
                product={product}
                defaultQty={1}
                ctaText={add_to_cart.text}
                customPrice={free_price ? price : false}
              />
            )}
          </div>
        </div>
      </Block>
      {description ? (
        <Block width="bleed" className="bg-white">
          <div
            id="content"
            ref={contentRef}
            className={contentClassNames}
            dangerouslySetInnerHTML={{ __html: description }}></div>
        </Block>
      ) : null}
    </>
  )
}

export default ProductSingle
